import { getResult } from "../../helpers/functional-util.js";
import { text } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import responsive from "../../responsive.js";

/**
 * `TabWidget` displays multiple contents in tabs.
 * Each content spec represent one of the tabs.
 *
 * @example
 *     tabWidget({
 *         tabs: [
 *             {
 *                 name: 'first tab',
 *                 id: 'first',
 *                 content: (html) => {}
 *             },
 *             {
 *                 name: 'second tab',
 *                 id: 'second',
 *                 content: 'Second tab.'
 *             }
 *         ]
 *     });
 * @param {object[]} tabs - List of tab specs. The first tab is selected by
 *   default unless currentTabId is provided
 * @param {string} [currentTabId] - Id of the tab selected by default
 * @param {boolean} [tabsOnlyOnMobile] - If true, render each tab
 *   content side by side on desktop and only display tabs on mobile devices.
 *   Otherwise, always render as tabs
 * @param {boolean} [hideLabelsOnMobile] - if true hides text in tabs
 *   on small screens, defaults to true
 * @param {boolean} [subTabs] - if true, render the tabs as a sub element
 *   within the page. Sub-tabs are not stretched and do not have an underline
 *   through the entire document
 * @param {function} [onTabClicked] - Called when clicking on another tab
 * @param {Renderable} [actions] - Anything that should be rendered
 *   alongside the tab names
 */
export default class TabWidget extends Widget {
	constructor({
		tabs,
		hideLabelsOnMobile,
		tabsOnlyOnMobile,
		currentTabId = tabs[0]?.id,
		subTabs = false,
		onTabClicked = () => {},
		actions,
	}) {
		super(...arguments);

		this._tabs = tabs;

		this._displayTabs = true;
		this._hideLabelsOnMobile = !!hideLabelsOnMobile;

		if (tabsOnlyOnMobile) {
			this._registerToBreakpoints();
		}

		this._currentTabId = currentTabId;
		this._subTabs = subTabs;
		this._onTabClicked = onTabClicked;
		this._actions = actions;
	}

	getActiveContent() {
		return this._activeTab().content;
	}

	getAllContents() {
		return this._tabs.map(({ content }) => content);
	}

	getAllTabs() {
		return this._tabs;
	}

	getTabById(id) {
		return this._tabs.find((tab) => tab.id === id);
	}

	setActiveTabWithId(id) {
		this._currentTabId = id;
		this.update();
	}

	renderContentOn(html) {
		html.div({ class: "TabWidget" }, (html) => {
			if (this._displayTabs) {
				this._renderTabsOn(html);
			} else {
				html.div({ class: "tab-content" }, (html) =>
					this._renderSideBySideOn(html),
				);
			}
		});
	}

	_renderTabsOn(html) {
		const enabledTabs = this._enabledTabs();

		html.render(
			this._tabButtonsRenderer(enabledTabs),
			this._tabContentRenderer(this._activeTab()),
		);
	}

	_tabButtonsRenderer(tabs) {
		return (html) =>
			html.div(
				{ class: "tab-buttons" },
				html.ul(
					{
						class: [
							"nav",
							"nav-tabs",
							`tabs-${tabs.length}`,
							{ subtabs: this._subTabs },
						],
						role: "tablist",
						"data-test": "tab-list",
					},
					tabs.map((tab) => this._tabButtonRenderer(tab)),
				),
				(html) =>
					this._actions &&
					html.div({ class: "actions" }, this._actions),
			);
	}

	_tabButtonRenderer(tab) {
		return (html) =>
			html.li(
				{
					class: [{ active: this._isTabActive(tab) }, tab.id],
					role: "presentation",
					"data-test": "tab-button",
				},
				html.a(
					{
						href: `#${tab.id}`,
						"aria-controls": tab.id,
						role: "tab",
						"data-toggle": "tab",
						id: `handler-${tab.id}`,
						"data-test": tab.dataTestName,
						"data-help": tab["data-help"],
						click: () => {
							this._currentTabId = tab.id;
							this.update();
							this._onTabClicked();
						},
					},
					this._tabLabelRenderer(tab),
				),
			);
	}

	_tabContentRenderer(tab) {
		return (html) =>
			html.div(
				{
					class: "tab-content",
					"data-test": "tab-content",
				},
				html.div(
					{
						class: "tab-pane active",
						role: "tabpanel",
						id: tab.id,
					},
					tab.content,
				),
			);
	}

	_renderSideBySideOn(html) {
		let currentRow;

		this._enabledTabs().forEach((tab, index) => {
			if (index % 2 === 0) {
				currentRow = html.div({
					class: "row",
				});
			}

			currentRow.appendBrush(
				html.div(
					{ class: "col-sm-6" },
					html.h2({ class: "section-title" }, this._getTabName(tab)),
					tab.content,
				),
			);
		});
	}

	_tabLabelRenderer(tab) {
		return (html) => {
			html.span(
				{
					class: { "hide-lg": this._hideLabelsOnMobile },
				},
				this._getTabName(tab),
			);
		};
	}

	_getTabName(tab) {
		return text({ ellipsis: true }, tab.name);
	}

	_showTabs() {
		this._displayTabs = true;
		this.update();
	}

	_hideTabs() {
		this._displayTabs = false;
		this.update();
	}

	_registerToBreakpoints() {
		responsive.registerToMobileBreakpoint({
			enterCallback: this._showTabs.bind(this),
			exitCallback: this._hideTabs.bind(this),
		});

		if (!responsive.isMobileBreakpoint()) {
			this._hideTabs();
		}
	}

	_enabledTabs() {
		return this._tabs.filter((tab) => {
			if (tab.enabled === undefined) {
				return true;
			}
			if (tab.enabled.isTabEnabled) {
				return tab.enabled.isTabEnabled(tab);
			}
			return getResult(tab.enabled);
		});
	}

	_isTabActive(tab) {
		return tab.id === this._currentTabId;
	}

	_activeTab() {
		return this._tabs.find(this._isTabActive.bind(this));
	}
}
